"use client";

import React, { useMemo } from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { useAdminUsers } from "@/lib/hooks/api/admin";
import type { AppUser } from "@/lib/types/database";
import {
  getActiveFilterEntries,
  getActiveAdvancedFilterEntries,
  type ActiveFilterEntry,
  type FilterNameResolver,
} from "./filterDisplayUtils";
import { isUserFilterField } from "../advanced-filters/utils/field-kind";
import type { SimpleTableConfig, BaseEntity } from "../../types";

interface AdvancedFilterGroupLike {
  id: string;
  field: string;
  operator: string;
  value: unknown;
}

interface SimpleTableHeaderActiveFiltersProps<TData extends BaseEntity> {
  config: SimpleTableConfig<TData>;
  searchValue: string;
  simpleFilters: Record<string, unknown>;
  advancedFilters: readonly AdvancedFilterGroupLike[];
  onRemoveSearch: () => void;
  onRemoveFilter: (fieldId: string) => void;
  onRemoveAdvancedFilter: (id: string) => void;
}

/**
 * Displays active filters and search as removable badges.
 * Each filter chip shows "Field: value"; clicking it opens a detail popover
 * (a drawer on mobile) with the full value list, so the meaning of an applied
 * saved view is legible directly from the toolbar. A native `title` gives a
 * quick hover hint without the extra tooltip layer.
 */
export function SimpleTableHeaderActiveFilters<TData extends BaseEntity>({
  config,
  searchValue,
  simpleFilters,
  advancedFilters,
  onRemoveSearch,
  onRemoveFilter,
  onRemoveAdvancedFilter,
}: SimpleTableHeaderActiveFiltersProps<TData>) {
  const { data: allUsers } = useAdminUsers();

  // Resolve user-reference filter values (e.g. Created By) to names so the chip
  // shows "Jane Doe" instead of an app_users UUID. Company/contact ids without a
  // cached name here fall back to a masked label (never the raw UUID).
  const resolveName = useMemo<FilterNameResolver>(() => {
    const nameById = new Map<string, string>();
    (allUsers ?? []).forEach((u: AppUser) => {
      if (u?.id) {
        const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "";
        if (name) nameById.set(u.id, name);
      }
    });
    return (fieldConfig, value) =>
      fieldConfig && isUserFilterField(fieldConfig) ? nameById.get(value) : undefined;
  }, [allUsers]);

  const entries = getActiveFilterEntries(simpleFilters, config.fields, resolveName);
  const advancedEntries = getActiveAdvancedFilterEntries(
    advancedFilters,
    config.fields,
    resolveName
  );

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {searchValue && (
        <Badge
          variant="secondary"
          className="h-7 pl-2 pr-1 py-1 flex items-center gap-1.5 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
        >
          <Search className="h-3 w-3 shrink-0" />
          <span className="text-2xs font-medium uppercase tracking-wide text-blue-500">Search</span>
          <span className="text-xs font-semibold max-w-[180px] truncate">{searchValue}</span>
          <button
            onClick={onRemoveSearch}
            className="ml-0.5 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-full p-0.5 transition-colors"
            type="button"
            aria-label="Remove search filter"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      )}

      {entries.map((entry) => (
        <FilterChip
          key={entry.fieldId}
          entry={entry}
          onRemove={() => onRemoveFilter(entry.fieldId)}
        />
      ))}

      {advancedEntries.map((entry) => (
        <FilterChip
          key={`advanced-${entry.id}`}
          entry={entry}
          operatorLabel={entry.operatorLabel}
          onRemove={() => onRemoveAdvancedFilter(entry.id)}
        />
      ))}
    </div>
  );
}

function FilterChip({
  entry,
  operatorLabel,
  onRemove,
}: {
  entry: Pick<ActiveFilterEntry, "label" | "parts">;
  /** Optional operator text shown between field and value (advanced filters). */
  operatorLabel?: string;
  onRemove: () => void;
}) {
  const { label, parts } = entry;
  const multiValue = parts.items.length > 1;
  const chipTitle = operatorLabel
    ? `${label} ${operatorLabel}: ${parts.items.join(", ")}`
    : `${label}: ${parts.items.join(", ")}`;

  return (
    <Badge
      variant="secondary"
      className="h-7 pl-1 pr-1 py-1 flex items-center gap-1 bg-muted hover:bg-muted/70 transition-colors"
    >
      <ResponsivePopover
        align="start"
        title={label}
        className="w-64"
        asChild
        content={
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
              {operatorLabel ? `${label} · ${operatorLabel}` : label}
            </div>
            {multiValue ? (
              <div className="flex flex-wrap gap-1.5">
                {parts.items.map((item, i) => (
                  <span
                    key={`${item}-${i}`}
                    className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                  >
                    {item}
                  </span>
                ))}
              </div>
            ) : (
              <div className="text-sm font-medium text-foreground">{parts.items[0]}</div>
            )}
          </div>
        }
      >
        <button
          type="button"
          title={chipTitle}
          className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm hover:bg-accent transition-colors max-w-[240px]"
          aria-label={`View ${label} filter details`}
        >
          <SlidersHorizontal className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="text-2xs font-medium uppercase tracking-wide text-muted-foreground shrink-0">
            {label}
          </span>
          {operatorLabel && (
            <span className="text-2xs font-medium lowercase text-muted-foreground/80 shrink-0">
              {operatorLabel}
            </span>
          )}
          <span className="text-xs font-semibold text-foreground truncate">{parts.summary}</span>
        </button>
      </ResponsivePopover>

      <button
        onClick={onRemove}
        className="hover:bg-accent rounded-full p-0.5 transition-colors shrink-0"
        type="button"
        aria-label={`Remove ${label} filter`}
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}
